// ============================================================================
// NESTED FLOW CALCULATIONS - Motor de Cálculo para Fluxos Aninhados (Sub-Processos)
// ============================================================================
// 
// Este módulo implementa a lógica recursiva para calcular métricas de fluxos
// que contêm sub-processos (cards do tipo 'sub_processo' com externalFlowId).
//
// Arquitetura:
// - Fluxo Pai contém cards normais + cards de sub-processo
// - Card de sub-processo aponta para um Fluxo Filho (externalFlowId)
// - O Lead Time do sub-processo = Lead Time total do Fluxo Filho
// - Suporta aninhamento infinito (sub-processo dentro de sub-processo)
//
// ============================================================================

import { CardData, SubProcessData, Sheet, Project } from '../types';
import { calculateNodeMetrics, NodeMetrics } from './calculations';

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Resultado do cálculo de métricas totais (suporta recursividade)
 */
export interface TotalMetricsResult {
  /** Lead Time total em minutos (soma de todos os TPs, incluindo sub-processos) */
  totalLeadTime: number;
  /** Touch Time total em minutos (soma de todos os TRAs, incluindo sub-processos) */
  totalTouchTime: number;
  /** Tempo de espera total (LT - TT) */
  totalWaitTime: number;
  /** Eficiência do fluxo (TT / LT * 100) */
  efficiency: number;
  /** Número total de atividades (incluindo sub-processos expandidos) */
  activityCount: number;
  /** Número de sub-processos encontrados */
  subProcessCount: number;
  /** Profundidade máxima de aninhamento */
  maxNestingDepth: number;
  /** IDs de todos os fluxos processados (para detectar loops) */
  processedFlowIds: string[];
  /** Informações do gargalo */
  bottleneck: {
    id: string;
    name: string;
    load: number;
    flowId: string;
  } | null;
  /** Mapa de métricas por card (para uso na UI) */
  cardMetricsMap: Map<string, NodeMetrics & { isSubProcess: boolean }>;
}

/**
 * Contexto de resolução para buscar fluxos por ID
 * Necessário para a recursividade funcionar
 */
export interface FlowResolver {
  /** Função que retorna as sheets de um projeto pelo ID */
  getProjectSheets: (projectId: string) => Sheet[] | null;
  /** Função que retorna todos os projetos disponíveis */
  getAllProjects: () => Project[];
  /** ID do projeto atual (para resolver referências relativas) */
  currentProjectId: string;
}

// ============================================================================
// CONSTANTES
// ============================================================================

/** Profundidade máxima de aninhamento para evitar loops infinitos */
const MAX_NESTING_DEPTH = 10;

/** Tempo disponível por dia em minutos (8 horas) */
const AVAILABLE_TIME_PER_DAY = 480;

// ============================================================================
// FUN!"ES AUXILIARES
// ============================================================================

/**
 * Converte lt (que pode estar em dias ou minutos) para minutos
 * Usa a mesma heurística do sistema principal
 */
function convertLtToMinutes(ltValue: number, hasUnit: boolean): number {
  if (ltValue === 0) return 0;
  
  if (hasUnit) {
    // Card novo: lt está em DIAS
    return ltValue * 24 * 60;
  } else {
    // Card legado: heurística
    return ltValue < 100 ? ltValue : ltValue * 24 * 60;
  }
}

/**
 * Extrai métricas básicas de um card (TP e TRA em minutos)
 */
function extractCardTimes(card: CardData): { leadTime: number; touchTime: number } {
  const ltValue = Number(card.lt) || 0;
  const leadTime = convertLtToMinutes(ltValue, !!card.tpUnit);
  const touchTime = Number(card.tt) || 0;
  
  return { leadTime, touchTime };
}

/**
 * Verifica se um card é do tipo sub-processo
 */
export function isSubProcessCard(card: CardData): boolean {
  return card.tipo === 'sub_processo' && !!card.externalFlowId;
}

/**
 * Verifica se um card foi criado por agrupamento
 */
export function isGroupedCard(card: CardData): boolean {
  return !!card.groupedCardIds && card.groupedCardIds.length > 0;
}

// ============================================================================
// CÁLCULO RECURSIVO DE MÉTRICAS
// ============================================================================

/**
 * Calcula métricas totais de um fluxo, resolvendo sub-processos recursivamente
 * 
 * @param cards - Array de cards do fluxo
 * @param resolver - Contexto para resolver referências a fluxos externos
 * @param currentDepth - Profundidade atual de aninhamento (para detectar loops)
 * @param processedIds - IDs de fluxos já processados (para detectar loops)
 * @returns Métricas totais calculadas
 */
export function calculateTotalMetrics(
  cards: CardData[],
  resolver?: FlowResolver,
  currentDepth: number = 0,
  processedIds: Set<string> = new Set()
): TotalMetricsResult {
  // Inicializar resultado
  const result: TotalMetricsResult = {
    totalLeadTime: 0,
    totalTouchTime: 0,
    totalWaitTime: 0,
    efficiency: 0,
    activityCount: 0,
    subProcessCount: 0,
    maxNestingDepth: currentDepth,
    processedFlowIds: Array.from(processedIds),
    bottleneck: null,
    cardMetricsMap: new Map(),
  };

  // Verificar profundidade máxima
  if (currentDepth > MAX_NESTING_DEPTH) {
    console.warn(`[NestedFlow] Profundidade máxima de aninhamento atingida (${MAX_NESTING_DEPTH})`);
    return result;
  }

  // Filtrar apenas cards ativos
  const activeCards = cards.filter(c => c.status === 'ativo' && c.tipo !== 'anotacao');

  // Processar cada card
  for (const card of activeCards) {
    // Pular card inicial (não contribui para tempo)
    if (card.tipo === 'card_inicial') continue;

    // Card de Sub-Processo: Resolver recursivamente
    if (isSubProcessCard(card) && resolver) {
      result.subProcessCount++;
      
      const subProcessMetrics = resolveSubProcessMetrics(
        card,
        resolver,
        currentDepth + 1,
        processedIds
      );

      if (subProcessMetrics) {
        // Somar métricas do sub-processo
        result.totalLeadTime += subProcessMetrics.totalLeadTime;
        result.totalTouchTime += subProcessMetrics.totalTouchTime;
        result.activityCount += subProcessMetrics.activityCount;
        result.maxNestingDepth = Math.max(result.maxNestingDepth, subProcessMetrics.maxNestingDepth);
        
        // Atualizar processedFlowIds
        subProcessMetrics.processedFlowIds.forEach(id => {
          if (!result.processedFlowIds.includes(id)) {
            result.processedFlowIds.push(id);
          }
        });

        // Verificar gargalo do sub-processo
        if (subProcessMetrics.bottleneck && 
            (!result.bottleneck || subProcessMetrics.bottleneck.load > result.bottleneck.load)) {
          result.bottleneck = subProcessMetrics.bottleneck;
        }

        // Registrar métricas do card de sub-processo
        result.cardMetricsMap.set(card.id, {
          capacity: null,
          fteTheoretical: 0,
          occupancy: 0,
          monthlyCost: 0,
          cycleTime: subProcessMetrics.totalTouchTime,
          leadTime: subProcessMetrics.totalLeadTime,
          waitTime: subProcessMetrics.totalLeadTime - subProcessMetrics.totalTouchTime,
          incomingVolume: card.incomingVolume || 0,
          isSubProcess: true,
        });
      }
    } 
    // Card Normal: Calcular diretamente
    else {
      const { leadTime, touchTime } = extractCardTimes(card);
      
      result.totalLeadTime += leadTime;
      result.totalTouchTime += touchTime;
      result.activityCount++;

      // Calcular métricas completas do nó
      const nodeMetrics = calculateNodeMetrics(card);
      result.cardMetricsMap.set(card.id, {
        ...nodeMetrics,
        isSubProcess: false,
      });

      // Verificar se é gargalo (maior loadFactor)
      if (nodeMetrics.loadFactor && 
          (!result.bottleneck || nodeMetrics.loadFactor > result.bottleneck.load)) {
        result.bottleneck = {
          id: card.id,
          name: card.atividade || 'Sem nome',
          load: nodeMetrics.loadFactor,
          flowId: 'current',
        };
      }
    }
  }

  // Calcular métricas derivadas
  result.totalWaitTime = Math.max(0, result.totalLeadTime - result.totalTouchTime);
  result.efficiency = result.totalLeadTime > 0 
    ? (result.totalTouchTime / result.totalLeadTime) * 100 
    : 0;

  return result;
}

/**
 * Resolve as métricas de um card de sub-processo buscando o fluxo filho
 */
function resolveSubProcessMetrics(
  card: CardData,
  resolver: FlowResolver,
  depth: number,
  processedIds: Set<string>
): TotalMetricsResult | null {
  if (!card.externalFlowId) return null;

  // Detectar loop
  if (processedIds.has(card.externalFlowId)) {
    console.warn(`[NestedFlow] Loop detectado! Fluxo ${card.externalFlowId} já foi processado.`);
    return null;
  }

  // Marcar como processado
  const newProcessedIds = new Set(processedIds);
  newProcessedIds.add(card.externalFlowId);

  // Se já temos dados em cache e são recentes, usar cache
  if (card.subProcessData) {
    const lastSync = new Date(card.subProcessData.lastSync);
    const now = new Date();
    const cacheAge = (now.getTime() - lastSync.getTime()) / 1000 / 60; // em minutos
    
    // Cache válido por 5 minutos
    if (cacheAge < 5) {
      return {
        totalLeadTime: card.subProcessData.totalLeadTime,
        totalTouchTime: card.subProcessData.totalTouchTime,
        totalWaitTime: card.subProcessData.totalLeadTime - card.subProcessData.totalTouchTime,
        efficiency: card.subProcessData.efficiency,
        activityCount: card.subProcessData.activityCount,
        subProcessCount: 0,
        maxNestingDepth: depth,
        processedFlowIds: Array.from(newProcessedIds),
        bottleneck: card.subProcessData.hasBottleneck ? {
          id: card.subProcessData.bottleneckId || '',
          name: card.subProcessData.bottleneckName || '',
          load: 0,
          flowId: card.externalFlowId,
        } : null,
        cardMetricsMap: new Map(),
      };
    }
  }

  // Buscar o fluxo filho
  // Formato do externalFlowId: "projectId:sheetId" ou apenas "sheetId" (mesmo projeto)
  let sheets: Sheet[] | null = null;
  let targetSheetId: string;

  if (card.externalFlowId.includes(':')) {
    const [projectId, sheetId] = card.externalFlowId.split(':');
    sheets = resolver.getProjectSheets(projectId);
    targetSheetId = sheetId;
  } else {
    sheets = resolver.getProjectSheets(resolver.currentProjectId);
    targetSheetId = card.externalFlowId;
  }

  if (!sheets) {
    console.warn(`[NestedFlow] Projeto não encontrado para fluxo ${card.externalFlowId}`);
    return null;
  }

  const targetSheet = sheets.find(s => s.id === targetSheetId);
  if (!targetSheet) {
    console.warn(`[NestedFlow] Sheet não encontrada: ${targetSheetId}`);
    return null;
  }

  // Calcular recursivamente
  return calculateTotalMetrics(
    targetSheet.cards,
    resolver,
    depth,
    newProcessedIds
  );
}

// ============================================================================
// FUN!"ES DE AGRUPAMENTO DE CARDS
// ============================================================================

/**
 * Agrupa múltiplos cards em um único card de sub-processo
 * 
 * @param cards - Array de cards selecionados para agrupar
 * @param allCards - Todos os cards do fluxo atual
 * @param subProcessName - Nome do novo sub-processo
 * @returns Novo array de cards com o sub-processo substituindo os originais
 */
export function groupCardsIntoSubProcess(
  selectedCards: CardData[],
  allCards: CardData[],
  subProcessName: string
): { updatedCards: CardData[]; newSubProcess: CardData } | null {
  if (selectedCards.length < 2) {
    console.warn('[NestedFlow] Selecione pelo menos 2 cards para agrupar');
    return null;
  }

  // x TRAVA DE SEGURAN!A: Não permitir agrupar cards marcadores (Início/Fim)
  const MARKER_TYPES = ['card_inicial', 'card_final'];
  const hasMarkerCard = selectedCards.some(c => MARKER_TYPES.includes(c.tipo));
  if (hasMarkerCard) {
    console.warn('[NestedFlow] Não é possível agrupar cards marcadores (Início/Fim)');
    return null;
  }

  const selectedIds = new Set(selectedCards.map(c => c.id));

  // x} CALCULAR POSI!ÒO ALINHADA: usar o card mais à esquerda como referência
  // Isso garante que o grupo fique alinhado com o fluxo de entrada (sem degraus nas setas)
  const leftmostCard = selectedCards.reduce((left, card) => 
    card.posicao.x < left.posicao.x ? card : left
  , selectedCards[0]);
  
  // Posição do grupo = posição do card de entrada (mais à esquerda)
  const groupPosition = {
    x: leftmostCard.posicao.x,
    y: leftmostCard.posicao.y
  };

  // Calcular métricas agregadas
  let totalLT = 0;
  let totalTT = 0;
  
  for (const card of selectedCards) {
    const { leadTime, touchTime } = extractCardTimes(card);
    totalLT += leadTime;
    totalTT += touchTime;
  }

  // Criar novo card de sub-processo (agrupado localmente)
  const newSubProcess: CardData = {
    id: `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    tipo: 'sub_processo',
    status: 'ativo',
    atividade: subProcessName,
    posicao: groupPosition, // x} Usar posição do card de entrada
    conexoes_saida: [],
    // Dados calculados do agrupamento
    lt: totalLT / (24 * 60), // Converter de volta para dias
    tt: totalTT,
    tpUnit: 'dias',
    // Metadados do agrupamento
    groupedCardIds: selectedCards.map(c => c.id),
    subProcessData: {
      flowName: subProcessName,
      totalLeadTime: totalLT,
      totalTouchTime: totalTT,
      activityCount: selectedCards.length,
      efficiency: totalLT > 0 ? (totalTT / totalLT) * 100 : 0,
      lastSync: new Date().toISOString(),
      hasBottleneck: false,
    },
  };

  // Reconectar: encontrar conexões de entrada e saída do grupo
  // Conexões de entrada: cards fora do grupo que apontam para cards dentro do grupo
  // Conexões de saída: cards dentro do grupo que apontam para cards fora do grupo
  
  const incomingConnections: { sourceId: string; sourceHandle: 'right' | 'bottom' }[] = [];
  const outgoingConnections: { targetId: string }[] = [];

  for (const card of allCards) {
    if (card.status !== 'ativo') continue;

    for (const conn of card.conexoes_saida) {
      const isSourceInGroup = selectedIds.has(card.id);
      const isTargetInGroup = selectedIds.has(conn.targetId);

      if (!isSourceInGroup && isTargetInGroup) {
        // Conexão de entrada no grupo
        incomingConnections.push({ sourceId: card.id, sourceHandle: conn.sourceHandle });
      } else if (isSourceInGroup && !isTargetInGroup) {
        // Conexão de saída do grupo
        outgoingConnections.push({ targetId: conn.targetId });
      }
    }
  }

  // Atualizar cards: remover selecionados, adicionar sub-processo
  const updatedCards = allCards
    .filter(c => !selectedIds.has(c.id))
    .map(card => {
      // Redirecionar conexões de entrada para o novo sub-processo
      const newConnections = card.conexoes_saida.map(conn => {
        if (selectedIds.has(conn.targetId)) {
          return {
            ...conn,
            id: `conn_${card.id}_${newSubProcess.id}`,
            targetId: newSubProcess.id,
          };
        }
        return conn;
      });

      return { ...card, conexoes_saida: newConnections };
    });

  // Adicionar conexões de saída ao sub-processo
  newSubProcess.conexoes_saida = outgoingConnections.map((conn, idx) => ({
    id: `conn_${newSubProcess.id}_${conn.targetId}_${idx}`,
    targetId: conn.targetId,
    sourceHandle: 'right' as const,
  }));

  // Adicionar o novo sub-processo
  updatedCards.push(newSubProcess);

  return { updatedCards, newSubProcess };
}

/**
 * Desagrupa um card de sub-processo, restaurando os cards originais
 * 
 * @param subProcessCard - Card de sub-processo a ser desagrupado
 * @param allCards - Todos os cards do fluxo atual
 * @param originalCards - Cards originais que foram agrupados (armazenados separadamente)
 * @returns Novo array de cards com os originais restaurados
 */
export function ungroupSubProcess(
  subProcessCard: CardData,
  allCards: CardData[],
  originalCards: CardData[]
): CardData[] | null {
  if (!isGroupedCard(subProcessCard)) {
    console.warn('[NestedFlow] Este sub-processo não foi criado por agrupamento');
    return null;
  }

  // Remover o sub-processo e adicionar os cards originais
  const updatedCards = allCards.filter(c => c.id !== subProcessCard.id);

  // Reconectar: cards que apontavam para o sub-processo devem apontar para o primeiro card original
  // E o sub-processo que apontava para outros deve ser substituído pelo último card original
  
  // Assumindo que originalCards mantêm suas conexões originais
  return [...updatedCards, ...originalCards];
}

// ============================================================================
// SINCRONIZA!ÒO DE SUB-PROCESSOS
// ============================================================================

/**
 * Atualiza os dados de cache de todos os sub-processos em um fluxo
 * Deve ser chamado quando um fluxo filho é modificado
 * 
 * @param cards - Cards do fluxo a atualizar
 * @param resolver - Contexto para resolver referências
 * @returns Cards atualizados com subProcessData sincronizado
 */
export function syncSubProcessData(
  cards: CardData[],
  resolver: FlowResolver
): CardData[] {
  return cards.map(card => {
    if (!isSubProcessCard(card)) return card;

    const metrics = resolveSubProcessMetrics(card, resolver, 0, new Set());
    
    if (!metrics) return card;

    const updatedSubProcessData: SubProcessData = {
      flowName: card.subProcessData?.flowName || card.atividade || 'Sub-processo',
      totalLeadTime: metrics.totalLeadTime,
      totalTouchTime: metrics.totalTouchTime,
      activityCount: metrics.activityCount,
      efficiency: metrics.efficiency,
      lastSync: new Date().toISOString(),
      hasBottleneck: !!metrics.bottleneck,
      bottleneckId: metrics.bottleneck?.id,
      bottleneckName: metrics.bottleneck?.name,
    };

    return {
      ...card,
      // Atualizar lt/tt com valores calculados
      lt: metrics.totalLeadTime / (24 * 60),
      tt: metrics.totalTouchTime,
      subProcessData: updatedSubProcessData,
    };
  });
}

// ============================================================================
// HELPERS PARA UI
// ============================================================================

/**
 * Formata o tempo total de forma legível
 */
export function formatTotalTime(minutes: number): string {
  if (minutes < 60) {
    return `${minutes.toFixed(0)} min`;
  } else if (minutes < 1440) {
    const hours = minutes / 60;
    return `${hours.toFixed(1)} h`;
  } else {
    const days = minutes / 1440;
    return `${days.toFixed(1)} dias`;
  }
}

/**
 * Retorna informações de resumo para exibir no tooltip de um sub-processo
 */
export function getSubProcessSummary(card: CardData): string {
  if (!card.subProcessData) {
    return 'Sub-processo não sincronizado';
  }

  const { flowName, activityCount, totalLeadTime, totalTouchTime, efficiency } = card.subProcessData;
  
  return [
    `📋 ${flowName}`,
    `📊 ${activityCount} atividades`,
    `⏱️ Lead Time: ${formatTotalTime(totalLeadTime)}`,
    `⚡ Touch Time: ${formatTotalTime(totalTouchTime)}`,
    `📈 Eficiência: ${efficiency.toFixed(1)}%`,
  ].join('\n');
}

/**
 * Verifica se um card pode ser navegado (drill-down)
 */
export function canDrillDown(card: CardData): boolean {
  return card.tipo === 'sub_processo' && !!card.externalFlowId;
}

/**
 * Extrai informações de navegação de um card de sub-processo
 */
export function getDrillDownInfo(card: CardData): { projectId: string; sheetId: string } | null {
  if (!canDrillDown(card)) return null;
  
  const externalFlowId = card.externalFlowId!;
  
  if (externalFlowId.includes(':')) {
    const [projectId, sheetId] = externalFlowId.split(':');
    return { projectId, sheetId };
  }
  
  return { projectId: 'current', sheetId: externalFlowId };
}

